import React, { useEffect, useMemo, useState } from 'react';
import { useData } from '../../hooks/useData';
import { MetricStrip } from '../common/MetricStrip';
import { EmptyState } from '../common/EmptyState';
import { Tag } from '../common/Tag';
import { fmtDate } from '../../utils/helpers';
import FeasibilityCreateModal from './FeasibilityCreateModal';
import { feasibilityAssessmentServices } from '../../APIServices/feasibilityAssessmentServices';
import { studyManagementServices } from '../../APIServices/studyManagementServices';
import Pagination from '../../hooks/Pagination';

function FeasibilityListPage({ onOpen }) {
  const { state, setState } = useData();
  const [showCreate, setShowCreate] = useState(false);
  const [feasibilityList, setFeasibilityList] = useState([]);
  const [feasibilityCount, setFeasibilityCount] = useState(0);
  const [studyList, setStudyList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [overviewRows, setOverviewRows] = useState([]);

  const totalPage = Math.ceil(feasibilityCount / itemsPerPage);

  const studiesMap = useMemo(() => {
    const map = {};
    (studyList || []).forEach((study) => {
      if (study?.studyId) {
        map[String(study.studyId)] = study;
      }
      if (study?._id) {
        map[String(study._id)] = study;
      }
    });
    return map;
  }, [studyList]);

  const fetchStudies = async () => {
    try {
      const data = await studyManagementServices.getStudyManagement('all', '', 1, 1000);
      setStudyList(data?.data || []);
    } catch (error) {
      setStudyList([]);
    }
  };

  const fetchFeasibilities = async () => {
    setLoading(true);
    try {
      const [data, overviewData] = await Promise.all([
        feasibilityAssessmentServices.getFeasibilityAssessmentData(
          statusFilter,
          debouncedSearchText,
          pageNumber,
          itemsPerPage
        ),
        feasibilityAssessmentServices.getFeasibilityAssessmentData(
          'all',
          '',
          1,
          5000
        ),
      ]);

      const rows = data?.data || [];
      setFeasibilityList(rows);
      setFeasibilityCount(data?.count || 0);
      setOverviewRows(overviewData?.data || []);

      setState((prev) => ({
        ...prev,
        feasibilities: rows.map((row) => ({
          id: row.assessmentId,
          assessmentId: row.assessmentId,
          code: row.code,
          title: row.title,
          studyId: row.studyId,
          cros: row.cros || [],
          dueAt: row.dueAt,
          status: row.status,
          version: row.version,
          responses: row.responses || {},
          comments: row.comments || [],
          recommendedCRO: row.recommendedCRO,
          rationale: row.rationale,
          approvedAt: row.approvedAt,
          approvedBy: row.approvedBy,
          locked: row.locked,
        })),
      }));
    } catch (error) {
      setFeasibilityList([]);
      setFeasibilityCount(0);
      setOverviewRows([]);
    } finally {
      setLoading(false);
    }
  };

  const feasibilityStats = useMemo(() => {
    const allRows = overviewRows || [];
    const approvedRows = allRows.filter((f) => String(f.status || '').toLowerCase() === 'approved');
    const openRows = allRows.filter((f) => String(f.status || '').toLowerCase() !== 'approved');
    const uniqueCros = new Set(allRows.flatMap((f) => f.cros || []));

    const avgApprovalDays = approvedRows.length
      ? Math.round(
          approvedRows.reduce((sum, row) => {
            const approvedAt = row.approvedAt ? new Date(row.approvedAt).getTime() : null;
            const createdAt = row.createdAt ? new Date(row.createdAt).getTime() : null;
            if (!approvedAt || !createdAt || Number.isNaN(approvedAt) || Number.isNaN(createdAt)) {
              return sum;
            }
            const diffDays = Math.max(0, (approvedAt - createdAt) / (1000 * 60 * 60 * 24));
            return sum + diffDays;
          }, 0) / approvedRows.length
        )
      : 0;

    return {
      open: openRows.length,
      approved: approvedRows.length,
      croCount: uniqueCros.size,
      avgApprovalDays,
    };
  }, [overviewRows]);

  useEffect(() => {
    fetchStudies();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageNumber(1);
      setDebouncedSearchText(searchText);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    fetchFeasibilities();
  }, [statusFilter, pageNumber, itemsPerPage, debouncedSearchText]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Vendor evaluation</div>
          <h1 className="page-title">Feasibility & CRO assessment</h1>
          <p className="page-sub">Weighted scoring, risk classification, comparative CRO evaluation. Approved versions lock for audit.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <i className="ti ti-plus" /> New feasibility
        </button>
      </div>

      <MetricStrip
        items={[
          { label: 'Open', value: feasibilityStats.open, foot: 'in progress or review' },
          { label: 'Approved', value: feasibilityStats.approved, foot: 'locked, archived' },
          { label: 'CROs evaluated', value: feasibilityStats.croCount, foot: 'across all assessments' },
          {
            label: 'Avg approval time',
            value: `${feasibilityStats.avgApprovalDays}d`,
            foot: feasibilityStats.approved > 0 ? 'based on approved assessments' : 'no approved assessments yet'
          }
        ]}
      />

      <div className="section-label mt-4 mb-5">
        Active assessments <span className="count">{feasibilityCount}</span>
      </div>

      <div className="card">
        <div className="flex gap-2 mb-3" style={{ padding: '14px 18px 0' }}>
          <div style={{ position: 'relative', flex: '0 0 260px' }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', fontSize: 13 }} />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search assessment..."
              style={{ width: '100%', padding: '7px 10px 7px 30px', border: '1px solid var(--line)', borderRadius: 2, fontSize: 12 }}
            />
          </div>
          <select className="btn btn-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="in_progress">In progress</option>
            <option value="in_review">In review</option>
            <option value="approved">Approved</option>
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="buttonLoader" />
            <div style={{ marginTop: 12 }}>Loading feasibility assessments...</div>
          </div>
        ) : feasibilityList.length === 0 ? (
          <EmptyState
            icon="ti-checklist"
            title="No feasibility assessments yet"
            desc="Create your first feasibility to begin CRO evaluation."
            action={<button className="btn btn-primary" onClick={() => setShowCreate(true)}><i className="ti ti-plus" /> New feasibility</button>}
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Study</th>
                <th>CROs</th>
                <th>Status</th>
                <th>Due</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {feasibilityList.map((f) => {
                const study = studiesMap[String(f.studyId)];
                return (
                  <tr
                    key={f.assessmentId || f._id}
                    className="clickable-row"
                    onClick={() => onOpen(f.assessmentId)}
                  >
                    <td className="id">{f.code}</td>
                    <td className="medium">{f.title}</td>
                    <td className="id">{study?.studyCode || '—'}</td>
                    <td>{(f.cros || []).length}</td>
                    <td>
                      {f.status === 'approved' ? (
                        <Tag variant="ok" dot>Approved · v{f.version}</Tag>
                      ) : f.status === 'in_review' ? (
                        <Tag variant="warn" dot>In review</Tag>
                      ) : (
                        <Tag dot>In progress</Tag>
                      )}
                    </td>
                    <td className="xs muted">{f.dueAt ? fmtDate(f.dueAt) : '—'}</td>
                    <td><i className="ti ti-chevron-right clickable-row-arrow" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <Pagination
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          totalPages={totalPage}
          data={feasibilityList}
          dataCount={feasibilityCount}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
        />
      </div>

      <FeasibilityCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        studyOptions={studyList}
        onCreated={(assessmentId) => {
          fetchFeasibilities();
          if (assessmentId) {
            onOpen(assessmentId);
          }
        }}
      />

      <style>{`
        .clickable-row {
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .clickable-row:hover {
          background: var(--surface-2);
        }

        .clickable-row-arrow {
          color: var(--ink-3);
          font-size: 14px;
          opacity: 0.75;
        }

        .clickable-row:hover .clickable-row-arrow {
          opacity: 1;
          color: var(--ink);
        }
      `}</style>
    </div>
  );
}

export default FeasibilityListPage;